<template>
  <app-page class="stripe-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026: „jetzt bitte alle shop seiten überarbeiten“):
         leichter Titel, Aktionen mit Beschriftung (Handy: Icon-Knöpfe); links Monat und Suche, rechts der Filter statt
         der Filterzeile -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
    </template>
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="In Zahlungen suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :time-defs="filterTimes"
        :time-days="timeDays"
        :active-filters="activeFilterCount"
        range-label="Brutto"
        :result-nouns="resultNouns"
        :empty-value="emptyStripeFilters"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <!-- Dialog liegt über der Seite und nimmt keinen Platz im Inhalt ein -->
    <app-dialog v-model="refundDialog" persistent :max-width="600">
      <template #title
        >Zahlung zu Bestellung #{{ refundData ? refundData.order_id : "" }}
        zurückerstatten</template
      >
      <p v-if="refundErrorMsg" class="app-field__error refund-error" role="alert">
        <app-icon name="alert" :size="14" />{{ refundErrorMsg }}
      </p>
      <div class="app-form-field refund-amount">
        <label class="app-label" for="stripe-refund-amount">Betrag in €</label>
        <app-text-field
          id="stripe-refund-amount"
          v-model="refundAmount"
          type="number"
          step=".01"
          pattern="^\d*(\.\d{0,2})?$"
          :max="maxRefundAmount"
          :min="0"
        />
      </div>
      <template #actions>
        <app-button @click="refundDialog = false">Abbrechen</app-button>
        <app-button kind="danger" :loading="refundLoading" @click="refund"
          >Zahlung zurückerstatten ({{ refundAmountText }})</app-button
        >
      </template>
    </app-dialog>

    <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
    <div class="app-grid-fill stripe-grid" :class="{ 'stripe-grid--auto': handy }">
      <dx-data-grid
        :ref="gridRefName"
        :height="gridHeight"
        :data-source="dataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="true"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :hover-state-enabled="!gridTouch"
        :scrolling="scrolling"
        :element-attr="gridAttr"
        :remote-operations="false"
        @toolbar-preparing="onToolbarPreparing"
        @content-ready="onContentReady"
      >
        <DxColumn
          data-field="order_id"
          caption="Bestellung"
          :calculate-cell-value="calcOrderId"
        />
        <DxColumn
          data-field="payment_method_details_type"
          caption="Zahlungsart"
        />
        <DxColumn
          data-field="payment_details"
          caption="Zahlungsdetails"
          data-type="string"
          :calculate-cell-value="calcPaymentDetails"
        />
        <DxColumn
          data-field="amount_eur"
          caption="Brutto"
          data-type="number"
          format="######0.00"
          :calculate-cell-value="calcAmount"
        />
        <DxColumn
          data-field="fee_eur"
          caption="Gebühr"
          data-type="number"
          format="######0.00"
          :calculate-cell-value="calcFee"
        />
        <DxColumn
          data-field="net_eur"
          caption="Netto"
          data-type="number"
          format="######0.00"
          :calculate-cell-value="calcNet"
        />
        <DxColumn
          data-field="refunded_eur"
          caption="Zurückerstattet"
          data-type="number"
          format="######0.00"
          :calculate-cell-value="calcRefunded"
        />
        <DxColumn
          data-field="currency"
          caption="Währung"
          :calculate-cell-value="calcCurrency"
        />
        <DxColumn
          data-field="status"
          caption="Status"
          :calculate-cell-value="calcStatus"
        />
        <DxColumn
          data-field="created"
          caption="Zahlungszeit"
          data-type="datetime"
          format="dd.MM.yyyy HH:mm:ss"
          :calculate-cell-value="calcCreated"
        />
        <DxColumn
          data-field="refund_date"
          caption="Rückerstattungszeit"
          data-type="datetime"
          format="dd.MM.yyyy HH:mm:ss"
          :calculate-cell-value="calcRefundDate"
        />
        <DxColumn data-field="id" caption="ID" />
        <DxColumn type="buttons">
          <DxButton
            hint="Zahlung zurückerstatten"
            icon="undo"
            :visible="true"
            :on-click="openRefundDialog"
          />
        </DxColumn>

        <dx-search-panel :visible="false" />

        <dx-editing
          refresh-mode="full"
          :allow-adding="false"
          :allow-updating="false"
          :allow-deleting="false"
          mode="batch"
        />

        <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
        <dx-pager :visible="false" />
        <dx-paging :page-size="10" />
        <dx-export :enabled="false" />
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span>Brutto <strong>{{ sumText("gross") }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Netto <strong>{{ sumText("net") }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
        <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxButton,
  DxPaging,
  DxPager,
  DxEditing,
  DxSearchPanel,
  DxExport,
} from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import DataSource from "devextreme/data/data_source";
import axios from "axios";
import moment from "moment";
import { formatEuro, formatNumber, plural } from "@/utils/format";
import query from "devextreme/data/query";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import { emptyFilters, activeCount, buildPredicate, buildFacets, buildTimeDays, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { STRIPE_FILTER_CONFIG, STRIPE_STATUS_TEXT } from "@/components/shop/shopFilters";

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxButton,
    DxPager,
    DxPaging,
    DxEditing,
    DxSearchPanel,
    DxExport,
    SaleFilterMenu,
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach Filter und Suche, „4 von 24 …“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Zahlung", "Zahlungen");
    },
    summaryNoun() {
      return plural(this.summary.count, "Zahlung", "Zahlungen");
    },
    activeFilterCount() {
      return activeCount(this.filters, STRIPE_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, STRIPE_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },

    // Betrag im Knopf nach R6 („12,50 €“)
    refundAmountText() {
      return formatEuro(this.refundAmount);
    },
  },
  watch: {
    month: function () {
      this.refreshDataGrid();
    },
    refundAmount: function (newRefundAmount) {
      if (newRefundAmount > this.maxRefundAmount) {
        this.refundAmount = this.maxRefundAmount;
      }
    },
  },
  data() {
    return {
      gridRefName: "stripePaymentGrid",
      lastId: null,
      firstId: null,
      pagingDirection: null,
      hasMore: false,
      month: new Date().toISOString().substr(0, 7),
      searchText: "",
      // Filter-Panel (Kundenwunsch 13.10.2026): Zustand, Werte der Listen, Treffer nach Filter und Suche
      filters: emptyFilters(STRIPE_FILTER_CONFIG),
      filterSegments: STRIPE_FILTER_CONFIG.segments,
      filterTimes: STRIPE_FILTER_CONFIG.times,
      timeDays: {},
      resultNouns: ["Zahlung", "Zahlungen"],
      facets: [],
      resultCount: null,
      summary: { count: 0, loaded: 0, sums: {} },
      gridAttr: {},
      refundDialog: false,
      refundErrorMsg: null,
      refundLoading: false,
      refundData: null,
      refundAmount: 0,
      maxRefundAmount: 0,
      dataSource: new DataSource({
        load: async () => {
          const data = (
            await axios.get(
              `${this.$store.state.baseUrl}stripepayment?month=${moment(
                this.month + "-01"
              ).format("MM")}&year=${moment(this.month + "-01").format("YYYY")}`
            )
          ).data;
          this._rows = Array.isArray(data) ? data : [];
          this.refreshFacets();
          return data;
        },
      }),
    };
  },
  methods: {
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptyStripeFilters() {
      return emptyFilters(STRIPE_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "status") {
        return STRIPE_STATUS_TEXT[key] || key;
      }
      return key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, STRIPE_FILTER_CONFIG));
      this.timeDays = Object.freeze(buildTimeDays(this._rows || [], STRIPE_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptyStripeFilters();
      this.applyFilters();
      this.refreshFacets();
    },
    applyFilters() {
      const grid = this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance;
      if (grid) {
        const match = buildPredicate(this.filters, STRIPE_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      this.resultCount = count;
      const rows = this._rows || [];
      let visible = rows;
      const filter = grid.getCombinedFilter();
      if (filter && rows.length) {
        try {
          visible = query(rows).filter(filter).toArray();
        } catch (err) {
          visible = rows;
        }
      }
      const sums = {};
      sums.gross = visible.reduce((sum, row) => sum + (Number(row.amount) || 0), 0) / 100;
      sums.net = visible.reduce((sum, row) => sum + ((Number(row.amount) || 0) - (Number(row.application_fee_amount) || 0)), 0) / 100;
      this.summary = { count, loaded: Math.max(rows.length, count), sums };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    sumText(key) {
      return formatEuro(this.summary.sums[key] || 0);
    },
    calcOrderId(row) {
      return `#${row.order_id}`;
    },
    calcPaymentDetails(row) {
      switch (row.payment_method_details_type) {
        case "card":
          return `${row.payment_method_details_card_brand.toUpperCase()} ****${
            row.payment_method_details_card_last4
          }`;
        default:
          return null;
      }
    },
    calcAmount(row) {
      return row.amount / 100;
    },
    calcFee(row) {
      return row.application_fee_amount / 100;
    },
    calcNet(row) {
      return (row.amount - row.application_fee_amount) / 100;
    },
    calcRefunded(row) {
      return row.amount_refunded ? row.amount_refunded / 100 : null;
    },
    calcCurrency(row) {
      return row.currency.toUpperCase();
    },
    calcStatus(row) {
      switch (row.status) {
        case "succeeded":
          return "Erfolgreich";
        case "requires_payment_method":
          return "Unvollständig";
        default:
          return row.status;
      }
    },
    calcCreated(row) {
      return row.created ? moment.unix(row.created).toDate() : null;
    },
    calcRefundDate(row) {
      return row.refund_date ? moment.unix(row.refund_date).toDate() : null;
    },
    openRefundDialog(e) {
      const data = e.row.data;
      this.refundDialog = true;
      this.refundLoading = false;
      this.refundErrorMsg = null;
      this.refundData = data;
      this.refundAmount = data.amount / 100;
      this.maxRefundAmount = (data.amount - data.amount_refunded) / 100;
    },
    async refund() {
      try {
        this.refundLoading = true;

        await axios.put(`${this.$store.state.baseUrl}stripepayment`, {
          amount: Math.round(this.refundAmount * 100),
          id: this.refundData.id,
        });
        this.refundDialog = false;
        this.refundData = null;
        this.refreshDataGrid();
      } catch (e) {
        this.refundErrorMsg = e.message;
      } finally {
        this.refundLoading = false;
      }
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
  },
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.stripe-grid.stripe-grid--auto {
  flex: none;
}
.refund-amount {
  max-width: 200px;
}
p.refund-error {
  margin: 0 0 12px;
}
</style>
